import { AssistantRuntimeProvider, useAui, useAuiState } from "@assistant-ui/react";
import { SettingsIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, Outlet, useSearchParams } from "react-router-dom";
import { TooltipProvider } from "@/components/ui/molecules/tooltip";
import { useAuth } from "@/hooks/use-auth";
import { useChatRuntime } from "@/hooks/use-chat-runtime";
import { SettingsContext } from "@/hooks/use-settings";
import { isNewChatShortcut } from "@/lib/shortcuts";
import { AppSidebar } from "@/shared/app-sidebar";
import { SettingsDialog } from "@/shared/settings-dialog";
import { Wordmark } from "@/shared/wordmark";
import { WorkspaceSwitcher } from "@/shared/workspace-switcher";

const SIDEBAR_KEY = "kourai-sidebar-collapsed";

function getSavedSidebarCollapsed(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_KEY) === "true";
  } catch {
    return false;
  }
}

/** ⌘T or ⌘⇧O (Ctrl on Windows/Linux) starts a new chat from anywhere on the page, even while typing. */
function NewThreadShortcut() {
  const aui = useAui();
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.repeat || !isNewChatShortcut(e)) return;
      e.preventDefault();
      aui.threads().switchToNewThread();
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [aui]);
  return null;
}

/**
 * `#/?thread=<id>` opens that thread: the side panel's "Open in new tab" continues a chat here.
 * The parameter is dropped once the thread is open, so a reload doesn't switch back to it.
 */
function OpenThreadFromUrl() {
  const [params, setParams] = useSearchParams();
  const threadId = params.get("thread");
  const aui = useAui();
  // The list loads again once the session is there; switch when the thread is in it, or the
  // switch lands on a list that is about to be replaced.
  const listed = useAuiState(
    (s) =>
      !!threadId &&
      !s.threads.isLoading &&
      s.threads.threadItems.some((t) => t.remoteId === threadId || t.id === threadId),
  );
  useEffect(() => {
    if (!threadId || !listed) return;
    Promise.resolve(aui.threads().switchToThread(threadId))
      .catch((err: unknown) => console.error("[chat] could not open thread", err))
      .finally(() => {
        setParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete("thread");
            return next;
          },
          { replace: true },
        );
      });
  }, [threadId, listed, aui, setParams]);
  return null;
}

export function Layout() {
  const { isAuthenticated } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(getSavedSidebarCollapsed);
  const toggleSidebar = () => {
    const next = !sidebarCollapsed;
    setSidebarCollapsed(next);
    try {
      localStorage.setItem(SIDEBAR_KEY, String(next));
    } catch {
      // Storage can be unavailable (private mode); the toggle still works for this session.
    }
  };
  const [settingsOpen, setSettingsOpen] = useState(false);
  const runtime = useChatRuntime();

  return (
    <AssistantRuntimeProvider runtime={runtime}>
      <SettingsContext.Provider value={{ openSettings: () => setSettingsOpen(true) }}>
        <TooltipProvider>
          <NewThreadShortcut />
          <OpenThreadFromUrl />
          <div className="bg-background flex h-dvh w-full">
            <AppSidebar
              collapsed={sidebarCollapsed}
              onToggle={toggleSidebar}
              onOpenSettings={() => setSettingsOpen(true)}
            />
            <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
              <header className="flex h-12 shrink-0 items-center gap-3 border-b px-4 md:hidden">
                <Wordmark />
                <div className="ml-auto flex items-center gap-2">
                  {isAuthenticated ? (
                    <>
                      <WorkspaceSwitcher />
                      <button
                        type="button"
                        onClick={() => setSettingsOpen(true)}
                        className="size-8 flex items-center justify-center rounded-md hover:bg-accent transition-colors"
                        aria-label="Settings"
                      >
                        <SettingsIcon className="size-4 text-muted-foreground" />
                      </button>
                    </>
                  ) : (
                    <Link
                      to="/auth/signin"
                      className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
                    >
                      Sign in
                    </Link>
                  )}
                </div>
              </header>
              <main className="flex-1 overflow-hidden">
                <Outlet />
              </main>
            </div>
          </div>
        </TooltipProvider>
        <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
      </SettingsContext.Provider>
    </AssistantRuntimeProvider>
  );
}
